import { isOpenQuestionPin, openQuestionAllCorrectAnswers } from '@lessonup/pins-shared';
import {
  InitializedPlayerStoreState,
  inputAndDiscussPinPhases,
  inputPinPhases,
  OpenQuestionView,
} from '../../playerStore.types';
import { PlayerPinReducer, PlayerPinReducerFactory } from '../reducers.types';
import { nextPhase } from '../reducers.utils';

export type OpenQuestionReducer = PlayerPinReducer<OpenQuestionView>;

export const createOpenQuestionReducer: PlayerPinReducerFactory<OpenQuestionView> = (pin) => {
  if (isOpenQuestionPin(pin)) {
    if (openQuestionAllCorrectAnswers(pin.teacherSettings).length > 0) {
      return openQuestionWithCorrectAnswersReducer;
    }
  }
  return openQuestionWithoutCorrectAnswersReducer;
};

export const openQuestionWithoutCorrectAnswersReducer: OpenQuestionReducer = {
  phases: inputPinPhases,
  initial: (state, history) => {
    return {
      type: 'OPEN_QUESTION',
      phase: 'input',
      answerOrder: computeAnswerOrder(state, history),
      revealedAnswers: history?.revealedAnswers ?? [],
      isCorrectAnswersCardRevealed: history?.isCorrectAnswersCardRevealed ?? false,
    };
  },
  hasNextView: (state, currentView, direction) => {
    return nextPhase(currentView.phase, openQuestionWithoutCorrectAnswersReducer.phases, direction) !== null;
  },
  handleNextView: (state, prev, direction) => {
    if (!state.currentView) {
      return false;
    }

    const next = nextPhase(prev.phase, openQuestionWithoutCorrectAnswersReducer.phases, direction);
    if (next) {
      return {
        ...prev,
        type: 'OPEN_QUESTION',
        phase: next,
      };
    }
    return false;
  },
  onEntriesUpdate: (state, currentView) => {
    return {
      ...currentView,
      answerOrder: computeAnswerOrder(state, currentView),
    };
  },
};

export const openQuestionWithCorrectAnswersReducer: OpenQuestionReducer = {
  phases: inputAndDiscussPinPhases,
  initial: (state, history) => {
    return {
      type: 'OPEN_QUESTION',
      phase: 'input',
      answerOrder: computeAnswerOrder(state, history),
      revealedAnswers: history?.revealedAnswers ?? [],
      isCorrectAnswersCardRevealed: history?.isCorrectAnswersCardRevealed ?? false,
    };
  },
  hasNextView: (state, currentView, direction) => {
    return nextPhase(currentView.phase, openQuestionWithCorrectAnswersReducer.phases, direction) !== null;
  },
  handleNextView: (state, prev, direction) => {
    if (!state.currentView) {
      return false;
    }

    const next = nextPhase(prev.phase, openQuestionWithCorrectAnswersReducer.phases, direction);
    if (next) {
      return {
        ...prev,
        type: 'OPEN_QUESTION',
        phase: next,
      };
    }
    return false;
  },
  onNextView: async (state, prev, next, services, setBackgroundActionAction) => {
    if (prev.phase === 'input') {
      const action = () => services.apiService.openQuestion.checkPinStudentAnswers(state.currentPinId);
      setBackgroundActionAction?.(action);
      return action();
    }
    return null;
  },
  onEntriesUpdate: (state, currentView) => {
    return {
      ...currentView,
      answerOrder: computeAnswerOrder(state, currentView),
    };
  },
};

function computeAnswerOrder(state: InitializedPlayerStoreState, previousView: OpenQuestionView | null) {
  const previousOrder = previousView?.answerOrder ?? [];

  if (!state.assignmentEntries.length) return previousOrder;

  return state.assignmentEntries.reduce<string[]>(
    (newOrder, assignmentEntry) => {
      if (
        assignmentEntry.entries[state.currentPinId] &&
        assignmentEntry.entries[state.currentPinId]?.type === 'OPEN_QUESTION' &&
        !previousOrder.includes(assignmentEntry._id)
      ) {
        newOrder.push(assignmentEntry._id);
      }

      return newOrder;
    },
    [...previousOrder]
  );
}
